import React from "react";
import { connect, type ConnectedProps } from "react-redux";
import type { ApolloClient } from "@apollo/client";
import { withApollo, type WithApolloClient } from "@apollo/client/react/hoc";
import { isEmpty } from "lodash";
import { FiGlobe, FiGrid } from "react-icons/fi";
import IconHeader from "components/IconHeader";
import IconSubheader from "components/IconSubheader";
import IconTable from "components/IconTable";
import type { IconTableColumn } from "components/IconTable";
// AddOrgModal is connected; its own-props require companyId: string and an
// onSuccess(organization: unknown). This page passes the optional this.props
// .companyId and an onSuccess that consumes the value as Organization_t — cast
// to the loose own-prop shape so those values pass through unchanged.
import AddOrgModalConnected from "./AddOrgModal";
import Button from "components/Button";
import { getOrgForCompany } from "services/adminService";
import { Link } from "react-router-dom";
import IconSpinner from "components/IconSpinner";
// ChangeOrgLimitModal is connected; its own-props require companyLimit: number.
// This page passes organization.companyLimit (number | null) — cast to a loose
// own-prop shape so the nullable value passes through as the original JS did.
import ChangeOrgLimitModalConnected from "pages/dashboards/adminDashboard/employers/ChangeOrgLimitModal";
import type { GetOrganizationForCompanyQuery } from "types/graphql";

const AddOrgModal = AddOrgModalConnected as React.ComponentType<{
  onClose: () => void;
  companyId?: string;
  onSuccess: (org: Organization_t) => void;
}>;

const ChangeOrgLimitModal =
  ChangeOrgLimitModalConnected as React.ComponentType<{
    onClose: () => void;
    organizationId?: string;
    companyLimit?: number | null;
    onSuccess: (companyLimit: number) => void;
  }>;

type Organization_t = NonNullable<
  GetOrganizationForCompanyQuery["getOrganizationForCompany"]
>;
type LinkedCompany = NonNullable<
  NonNullable<Organization_t["linkedCompanies"]>[number]
>;

const connector = connect();
type PropsFromRedux = ConnectedProps<typeof connector>;

interface OwnProps {
  companyId?: string;
}

type Props = WithApolloClient<OwnProps & PropsFromRedux>;

interface State {
  showOrgModal: boolean;
  initialFetching: boolean;
  showChangeLimitModal: boolean;
  // written by setState in componentDidMount / modal onSuccess, read in render
  organization?: Organization_t | null;
}

class Organization extends React.PureComponent<Props, State> {
  constructor(props: Props) {
    super(props);

    this.state = {
      showOrgModal: false,
      initialFetching: true,
      showChangeLimitModal: false,
    };
  }

  async componentDidMount() {
    const organization = await getOrgForCompany(
      this.props.client as ApolloClient<object>,
      // companyId is optional on props (PropTypes) but always route-supplied at
      // runtime; the query variable is required
      this.props.companyId as string
    );

    this.setState({
      initialFetching: false,
      organization,
    });
  }

  _buildCompanyColumns = (): Array<IconTableColumn<LinkedCompany>> => {
    const columns: Array<IconTableColumn<LinkedCompany>> = [
      {
        label: "Company Name",
        key: "name",
      },
      {
        label: "Company ID",
        key: "id",
      },
      {
        label: "Company DBA",
        key: "dba",
      },
      {
        label: "Company EIN",
        key: "ein",
      },
      {
        label: "Company State",
        key: "currentState",
      },
      {
        customComponent: (props: LinkedCompany) => {
          if (props.id === this.props.companyId) {
            return null;
          }
          return (
            <div>
              <Link to={`/employers/${props.id}`} className="icon-link">
                View Company
              </Link>
            </div>
          );
        },
      },
    ];
    return columns;
  };

  _getOrganizationContent = () => {
    return (
      <>
        <div className="plan-table">
          <div className="plan-header">
            <div className="plan-avatar">
              <p className="plan-initials">
                <FiGrid
                  color="white"
                  stroke="#60A4BF"
                  stroke-width="1.5"
                  size={26}
                />
              </p>
            </div>
            <div className="plan-details">
              <p className="plan-name">{this.state.organization!.name}</p>
              <p className="detail">
                Organization Id: {this.state.organization!.id}
              </p>
              <p className="detail">
                Company Limit: {this.state.organization!.companyLimit}
                <span
                  className="icon-link"
                  style={{
                    marginLeft: 8,
                  }}
                  onClick={() => this.setState({ showChangeLimitModal: true })}
                >
                  change limit
                </span>
              </p>
            </div>
          </div>
        </div>
        <IconTable
          columns={this._buildCompanyColumns()}
          // linkedCompanies is Array<LinkedCompany | null> | null from codegen;
          // IconTable's map tolerates null/empty rows, matching original JS
          data={this.state.organization!.linkedCompanies as LinkedCompany[]}
        />
      </>
    );
  };

  _getContent = () => {
    const data = this.state.organization;

    if (!isEmpty(data)) {
      return this._getOrganizationContent();
    }

    return (
      <div className="action-box">
        <div className="employerInfo">
          <div className="employerInfo-body">
            <div>
              <div className="circle">
                <span className="icon">
                  <FiGlobe color="white" stroke="#60A4BF" size={16} />
                </span>
              </div>
            </div>
            <div className="employerInfo-intro">
              <IconHeader variant="labelHeader" headerText="Organization" />
              <IconSubheader subheader="Create a new organization" />
            </div>
          </div>
          <div className="employerInfo-action">
            <Button
              size="sm"
              name="action"
              btnLabel="Create Organization"
              onClick={() => this.setState({ showOrgModal: true })}
            />
          </div>
        </div>
      </div>
    );
  };

  render() {
    const content = this._getContent();

    if (this.state.initialFetching) return <IconSpinner centered />;

    return (
      <div className="organization-container">
        {this.state.showOrgModal && (
          <AddOrgModal
            onClose={() => this.setState({ showOrgModal: false })}
            companyId={this.props.companyId}
            onSuccess={(org) => {
              this.setState({ showOrgModal: false, organization: org });
            }}
          />
        )}
        {this.state.showChangeLimitModal && (
          <ChangeOrgLimitModal
            onClose={() => this.setState({ showChangeLimitModal: false })}
            organizationId={this.state.organization!.id}
            companyLimit={this.state.organization!.companyLimit}
            onSuccess={(companyLimit) => {
              this.setState({
                showChangeLimitModal: false,
                organization: {
                  ...this.state.organization!,
                  companyLimit,
                },
              });
            }}
          />
        )}
        {content}
      </div>
    );
  }
}

export default connector(withApollo<Props>(Organization));
